import React, { useContext, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Language } from "../../App";

/**
 * SEO & Structured Metadata component for the Agenda page.
 * Manages dynamic document title, meta tags (OpenGraph, Twitter, App Links, Robots),
 * and Schema.org JSON-LD structured data for rich search engine indexing.
 */
const AgendaSEO = ({
  viewMode = "explore",
  category = "all",
  events = [],
}) => {
  const { lang } = useContext(Language) || {};

  const isSpanish = useMemo(() => {
    try {
      if (typeof window !== "undefined") {
        const searchParams = new URLSearchParams(window.location.search);
        const urlLang = searchParams.get("lang");
        if (urlLang) {
          const lower = urlLang.toLowerCase().trim();
          if (lower === "es" || lower === "spanish" || lower === "sp") return true;
          if (lower === "en" || lower === "english") return false;
        }
        if (searchParams.has("es") || searchParams.has("spanish")) return true;
        if (window.location.pathname.toLowerCase().includes("/es")) return true;
      }
      const stored = localStorage.getItem("selectedLanguage");
      if (stored === "Spanish") return true;
      if (stored === "English") return false;
      return (
        lang?.code === "es" ||
        (typeof navigator !== "undefined" &&
          Boolean(navigator.language?.toLowerCase()?.startsWith("es")))
      );
    } catch {
      return false;
    }
  }, [lang]);

  const normalizedViewMode = useMemo(() => {
    const v = String(viewMode || "").toLowerCase().trim();
    if (v === "schedules" || v === "schedule" || v === "calendar") return "schedules";
    if (v === "saved") return "saved";
    if (v === "completed") return "completed";
    return "explore";
  }, [viewMode]);

  // Compute canonical URL & origin safely
  const { siteOrigin, canonicalUrl, pageUrl } = useMemo(() => {
    const origin =
      typeof window !== "undefined" && window.location.origin
        ? window.location.origin
        : "https://ophthalmologyradar.com";

    const basePath = isSpanish ? "/agenda/es" : "/agenda";
    let routePath = basePath;

    // Slash routing: Home is explore (/agenda or /agenda/es)
    // Other tabs are /agenda/schedules, /agenda/saved, /agenda/completed
    if (normalizedViewMode === "schedules") {
      routePath = `${basePath}/schedules`;
    } else if (normalizedViewMode === "saved") {
      routePath = `${basePath}/saved`;
    } else if (normalizedViewMode === "completed") {
      routePath = `${basePath}/completed`;
    }

    const params = new URLSearchParams();
    if (category && category !== "all") {
      params.set("category", category);
    }

    const queryString = params.toString();
    const cleanCanonical = `${origin}${routePath}`;
    const fullUrl = queryString ? `${cleanCanonical}?${queryString}` : cleanCanonical;

    return {
      siteOrigin: origin,
      canonicalUrl: cleanCanonical,
      pageUrl: fullUrl,
    };
  }, [category, normalizedViewMode, isSpanish]);

  // Default social share image (Ophthalmology Radar Agenda Banner 1200x630)
  const ogImage = useMemo(() => {
    const origin =
      typeof window !== "undefined" && window.location.origin
        ? window.location.origin
        : "https://ophthalmologyradar.com";

    return isSpanish ? `${origin}/agenda-banner-es.png` : `${origin}/agenda-banner.png`;
  }, [isSpanish]);

  // Category display label
  const categoryLabel = useMemo(() => {
    switch (category) {
      case "event":
        return lang?.events || (isSpanish ? "Eventos" : "Events");
      case "webinar":
        return lang?.webinars || (isSpanish ? "Webinarios" : "Webinars");
      case "clinical_session":
        return lang?.clinical_sessions || (isSpanish ? "Sesiones Clínicas" : "Clinical Sessions");
      case "mentorship":
        return lang?.mentorships || (isSpanish ? "Tutorías" : "Mentorships");
      default:
        return "";
    }
  }, [category, lang, isSpanish]);

  // Dynamic Title
  const title = useMemo(() => {
    if (normalizedViewMode === "schedules") {
      return (
        lang?.agenda_seo_title_schedules ||
        (isSpanish
          ? "Agendas Programadas | Ophthalmology Radar"
          : "Scheduled Agendas | Ophthalmology Radar")
      );
    }
    if (normalizedViewMode === "saved") {
      return (
        lang?.agenda_seo_title_saved ||
        (isSpanish
          ? "Agendas Guardadas | Ophthalmology Radar"
          : "Saved Agendas | Ophthalmology Radar")
      );
    }
    if (normalizedViewMode === "completed") {
      return (
        lang?.agenda_seo_title_completed ||
        (isSpanish
          ? "Agendas Completadas | Ophthalmology Radar"
          : "Completed Agendas | Ophthalmology Radar")
      );
    }

    // Default explore mode
    if (category && category !== "all" && categoryLabel) {
      return isSpanish
        ? `${categoryLabel} | Agenda | Ophthalmology Radar`
        : `${categoryLabel} | Agenda | Ophthalmology Radar`;
    }

    return (
      lang?.agenda_seo_title_default || "Agenda | Ophthalmology Radar"
    );
  }, [normalizedViewMode, category, categoryLabel, lang, isSpanish]);

  // Dynamic Description
  const description = useMemo(() => {
    if (normalizedViewMode === "schedules") {
      return (
        lang?.agenda_seo_desc_schedules ||
        (isSpanish
          ? "Consulta tus agendas médicas programadas y próximos calendarios en Ophthalmology Radar."
          : "View your scheduled medical agendas and upcoming calendar on Ophthalmology Radar.")
      );
    }
    if (normalizedViewMode === "saved") {
      return (
        lang?.agenda_seo_desc_saved ||
        (isSpanish
          ? "Accede a todas tus agendas médicas guardadas en un solo lugar en Ophthalmology Radar."
          : "Access all your saved medical agendas in one organized place on Ophthalmology Radar.")
      );
    }
    if (normalizedViewMode === "completed") {
      return (
        lang?.agenda_seo_desc_completed ||
        (isSpanish
          ? "Revisa tus agendas médicas completadas y sesiones anteriores en Ophthalmology Radar."
          : "Review your completed medical agendas and past sessions on Ophthalmology Radar.")
      );
    }

    return (
      lang?.agenda_seo_desc_default ||
      (isSpanish
        ? "Explora agendas médicas, webinarios de oftalmología, sesiones clínicas y eventos en Ophthalmology Radar."
        : "Explore medical agendas, ophthalmology webinars, clinical case sessions, and events on Ophthalmology Radar.")
    );
  }, [normalizedViewMode, lang, isSpanish]);

  // SEO Keywords
  const keywords = useMemo(() => {
    return (
      lang?.agenda_seo_keywords ||
      (isSpanish
        ? "eventos de oftalmología, seminarios web médicos, sesiones clínicas, congreso de oftalmología, calendario médico, red de oftalmólogos, educación médica continua, Ophthalmology Radar"
        : "ophthalmology events, medical webinars, clinical sessions, ophthalmology conference, doctor mentorship, medical education, ophthalmologist network, CME, Ophthalmology Radar")
    );
  }, [lang, isSpanish]);

  // JSON-LD Structured Data Schema (CollectionPage + MedicalSpecialty + Event ItemList)
  const schemaJson = useMemo(() => {
    const validEvents = Array.isArray(events)
      ? events.filter((e) => e && e.title).slice(0, 10)
      : [];

    const schemaObj = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: title,
      description: description,
      url: pageUrl,
      inLanguage: isSpanish ? "es-ES" : "en-US",
      isPartOf: {
        "@type": "WebSite",
        name: "Ophthalmology Radar",
        url: siteOrigin,
      },
      about: [
        {
          "@type": "MedicalSpecialty",
          name: "Ophthalmology",
        },
        {
          "@type": "Thing",
          name: "Continuing Medical Education",
        },
        {
          "@type": "Thing",
          name: "Medical Webinars & Clinical Sessions",
        },
      ],
    };

    if (validEvents.length > 0) {
      schemaObj.mainEntity = {
        "@type": "ItemList",
        name: title,
        numberOfItems: validEvents.length,
        itemListElement: validEvents.map((item, index) => {
          const isVirtual =
            item.type === "webinar" || item.type === "clinical_session";
          return {
            "@type": "ListItem",
            position: index + 1,
            item: {
              "@type": item.type === "webinar" ? "EducationEvent" : "Event",
              name: item.title,
              description: item.description || description,
              startDate: item.date || undefined,
              endDate: item.endDate || item.date || undefined,
              eventAttendanceMode: isVirtual
                ? "https://schema.org/OnlineEventAttendanceMode"
                : "https://schema.org/OfflineEventAttendanceMode",
              eventStatus: "https://schema.org/EventScheduled",
              organizer: {
                "@type": "Organization",
                name: "Ophthalmology Radar",
                url: siteOrigin,
              },
            },
          };
        }),
      };
    }

    return JSON.stringify(schemaObj);
  }, [title, description, pageUrl, siteOrigin, isSpanish, events]);

  return (
    <Helmet>
      {/* Primary HTML Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta
        name="robots"
        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />
      <meta name="language" content={isSpanish ? "Spanish" : "English"} />

      {/* Multilingual Alternate Links (hreflang for international SEO) */}
      <link
        rel="alternate"
        hrefLang="en"
        href={
          normalizedViewMode === "schedules"
            ? `${siteOrigin}/agenda/schedules`
            : normalizedViewMode === "saved"
            ? `${siteOrigin}/agenda/saved`
            : normalizedViewMode === "completed"
            ? `${siteOrigin}/agenda/completed`
            : `${siteOrigin}/agenda`
        }
      />
      <link
        rel="alternate"
        hrefLang="es"
        href={
          normalizedViewMode === "schedules"
            ? `${siteOrigin}/agenda/es/schedules`
            : normalizedViewMode === "saved"
            ? `${siteOrigin}/agenda/es/saved`
            : normalizedViewMode === "completed"
            ? `${siteOrigin}/agenda/es/completed`
            : `${siteOrigin}/agenda/es`
        }
      />
      <link
        rel="alternate"
        hrefLang="x-default"
        href={
          normalizedViewMode === "schedules"
            ? `${siteOrigin}/agenda/schedules`
            : normalizedViewMode === "saved"
            ? `${siteOrigin}/agenda/saved`
            : normalizedViewMode === "completed"
            ? `${siteOrigin}/agenda/completed`
            : `${siteOrigin}/agenda`
        }
      />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Ophthalmology Radar" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Medical Agenda &amp; Live Schedules | Ophthalmology Radar" />
      <meta property="og:locale" content={isSpanish ? "es_ES" : "en_US"} />
      <meta property="og:locale:alternate" content={isSpanish ? "en_US" : "es_ES"} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@MedicalRadar" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="Medical Agenda &amp; Live Schedules | Ophthalmology Radar" />

      {/* Deep Linking App Metadata */}
      <meta property="al:ios:app_name" content="Medicalradar" />
      <meta property="al:ios:app_store_id" content="1605786600" />
      <meta property="al:ios:url" content={pageUrl} />
      <meta property="al:android:app_name" content="MedicalRadar" />
      <meta
        property="al:android:package"
        content="com.drudotstech.medicalradar"
      />
      <meta property="al:android:url" content={pageUrl} />

      {/* Schema.org Structured Data */}
      <script type="application/ld+json">{schemaJson}</script>
    </Helmet>
  );
};

export default React.memo(AgendaSEO);
